import { NONCE_PREFIX_SIZE, NONCE_SIZE, OFF_NONCE_PREFIX, HEADER_SIZE } from "./constants.js";
import { LscError } from "./errors.js";

/** §5.3 — nonce_i = noncePrefix(7) || uint32BE(i) || lastFlag(1) */
export function buildNonce(noncePrefix: Uint8Array, index: number, isLast: boolean): Uint8Array {
  if (noncePrefix.length !== NONCE_PREFIX_SIZE) {
    throw new LscError("BAD_INPUT", `noncePrefix must be ${NONCE_PREFIX_SIZE} bytes`);
  }
  if (!Number.isInteger(index) || index < 0 || index > 0xffff_ffff) {
    throw new LscError("BAD_INPUT", "frame index out of uint32 range");
  }
  const nonce = new Uint8Array(NONCE_SIZE);
  nonce.set(noncePrefix, 0);
  new DataView(nonce.buffer).setUint32(NONCE_PREFIX_SIZE, index, false);
  nonce[NONCE_SIZE - 1] = isLast ? 0x01 : 0x00;
  return nonce;
}

function prefixOf(headerBytes: Uint8Array): Uint8Array {
  if (headerBytes.length !== HEADER_SIZE) {
    throw new LscError("BAD_INPUT", `header must be ${HEADER_SIZE} bytes`);
  }
  return headerBytes.subarray(OFF_NONCE_PREFIX, OFF_NONCE_PREFIX + NONCE_PREFIX_SIZE);
}

/** frame_i = AES-256-GCM(K_file, nonce_i, P_i, AAD = header) */
export async function encryptFrame(
  key: CryptoKey,
  headerBytes: Uint8Array,
  index: number,
  isLast: boolean,
  plaintext: Uint8Array,
): Promise<Uint8Array> {
  const nonce = buildNonce(prefixOf(headerBytes), index, isLast);
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce, additionalData: headerBytes, tagLength: 128 },
    key,
    plaintext,
  );
  return new Uint8Array(ct);
}

/** Inverse of encryptFrame. Any failure (tag, wrong index/flag, wrong key) → INTEGRITY_FAILED. */
export async function decryptFrame(
  key: CryptoKey,
  headerBytes: Uint8Array,
  index: number,
  isLast: boolean,
  frame: Uint8Array,
): Promise<Uint8Array> {
  const nonce = buildNonce(prefixOf(headerBytes), index, isLast);
  try {
    const pt = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: nonce, additionalData: headerBytes, tagLength: 128 },
      key,
      frame,
    );
    return new Uint8Array(pt);
  } catch {
    throw new LscError("INTEGRITY_FAILED", `frame ${index}`);
  }
}
